import { useEffect, useState } from 'react';
import {
  Anchor,
  Breadcrumbs,
  Button,
  Group,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Tabs,
  Title,
} from '@mantine/core';
import { useParams } from 'react-router-dom';
import { setCapture, type CaptureMode } from '../api/capture';
import { useConsoleNav } from '../shell/nav';
import {
  AutoGrid,
  ChipRail,
  ColLabel,
  ConsoleTable,
  EstatePill,
  KV,
  NotConnected,
  Pill,
  Row,
  SectionCard,
  TableMessage,
  uiClasses,
} from '../ui';
import { dateOnly, expiringSoon, listCost, relative } from '../ui/format';
import { tokens as T } from '../theme/tokens';
import {
  useClientKeysStore,
  useDeploymentsStore,
  useEstatesStore,
  useTenantsStore,
  useTokensStore,
} from '../stores/dataStores';
import { isUnconfigured, rowsOf } from '../stores/collection';
import { useUIStore, type EstateTab } from '../stores/uiStore';
import { effectiveBillingMode, tenantKey } from '../data/types';
import { DEFAULT_MEMORY_CAP } from '../data/memoryState';
import { getSources } from '../stores/sources';
import { NO_WRITE } from './PoolPage';

const TENANT_COLS = [
  { key: 'tenant', label: 'Tenant' },
  { key: 'pool', label: 'Credential pool', w: 140 },
  { key: 'deployments', label: 'Deployments', w: 130 },
  { key: 'billing', label: 'Billing', w: 120 },
  { key: 'cost', label: 'Cost · list', w: 120 },
  { key: 'status', label: 'Status', w: 100 },
];

const DEPLOY_COLS = [
  { key: 'deployment', label: 'Deployment' },
  { key: 'tenant', label: 'Tenant', w: 150 },
  { key: 'billing', label: 'Billing', w: 110 },
  { key: 'expires', label: 'Token expires', w: 140 },
  { key: 'fingerprint', label: 'Fingerprint', w: 120 },
  { key: 'actions', w: 90 },
];

const KEY_COLS = [
  { key: 'key', label: 'Client key' },
  { key: 'deployment', label: 'Deployment', w: 180 },
  { key: 'last', label: 'Last exchange', w: 140 },
  { key: 'expires', label: 'Expires', w: 150 },
  { key: 'actions', w: 90 },
];

export function EstatePage() {
  const { estateId = '' } = useParams();
  const navigate = useConsoleNav();
  const estates = useEstatesStore((s) => s.data);
  const deployments = useDeploymentsStore((s) => s.data);
  const openDeployments = useDeploymentsStore((s) => s.open);
  const closeDeployments = useDeploymentsStore((s) => s.close);
  const tokensCol = useTokensStore((s) => s.data);
  const openTokens = useTokensStore((s) => s.open);
  const closeTokens = useTokensStore((s) => s.close);
  const clientKeys = useClientKeysStore((s) => s.data);
  const openClientKeys = useClientKeysStore((s) => s.open);
  const closeClientKeys = useClientKeysStore((s) => s.close);
  const tenants = rowsOf(useTenantsStore((s) => s.data));
  const ui = useUIStore();
  const canMutate = getSources().canMutate;

  // Estates belongs to the Shell — the sidebar lists them on every screen — so
  // this page reads it and never opens or closes it.
  useEffect(() => {
    openDeployments(estateId);
    openTokens(estateId);
    openClientKeys(estateId);
    return () => {
      closeDeployments();
      closeTokens();
      closeClientKeys();
    };
  }, [estateId, openDeployments, openTokens, openClientKeys, closeDeployments, closeTokens, closeClientKeys]);

  const estate = estates.byId[estateId];

  // Capture is written through the admin API, and the estate row it changes
  // comes back on the Firestore watch — so there is no local copy to keep in
  // step. `pending` exists only to keep the chips from lying between the click
  // and the snapshot.
  const [pendingCapture, setPendingCapture] = useState<string | null>(null);
  const [captureError, setCaptureError] = useState('');
  // `||`, not `??`. The server's canonical "off" is the EMPTY STRING, and `??`
  // preserves it — so an estate with capture off selected no chip and fell
  // through to "Storing every completion", which is the worst possible thing
  // for this control to say wrongly.
  const captureMode = pendingCapture || estate?.capture?.mode || 'off';

  async function changeCapture(mode: string) {
    setPendingCapture(mode);
    setCaptureError('');
    try {
      await setCapture(estateId, {
        mode: mode as CaptureMode,
        // A sampled default of 10% rather than 0: a rate of zero is refused by
        // the server, so offering the chip without one would make it look
        // broken. An operator who wants another rate uses `haus capture set`.
        sampleRate: mode === 'sampled' ? (estate?.capture?.sampleRate || 0.1) : 0,
        retentionHours: estate?.capture?.retentionHours ?? 0,
      });
    } catch (err) {
      setPendingCapture(null);
      setCaptureError(err instanceof Error ? err.message : String(err));
    }
  }

  // Once the watch delivers the estate carrying the new mode, stop overriding.
  useEffect(() => {
    if (pendingCapture && estate?.capture?.mode === pendingCapture) setPendingCapture(null);
    if (pendingCapture === 'off' && !estate?.capture?.mode) setPendingCapture(null);
  }, [estate?.capture?.mode, pendingCapture]);

  const deployRows = rowsOf(deployments);
  const tokenRows = rowsOf(tokensCol);
  const keyRows = rowsOf(clientKeys);
  const tenantRows = tenants.filter((t) => t.estateId === estateId);

  const tokenFor = (deploymentId: string) =>
    tokenRows.find((t) => t.deploymentId === deploymentId && !t.revokedAt);

  if (!estate) {
    return (
      <Stack gap={16}>
        <Title order={2}>{estateId}</Title>
        <NotConnected what="this estate" />
      </Stack>
    );
  }

  const filteredTenants = tenantRows
    .filter((t) => (ui.tenantMode === 'all' ? true : t.billingMode === ui.tenantMode))
    .filter((t) =>
      ui.tenantPool === 'all'
        ? true
        : ui.tenantPool === 'own'
          ? t.sealedKeys > 0
          : t.sealedKeys === 0,
    )
    .filter((t) => {
      const q = ui.tenantSearch.trim().toLowerCase();
      return !q || t.displayName.toLowerCase().includes(q) || t.key.toLowerCase().includes(q);
    })
    .sort((a, b) =>
      ui.tenantSort === 'name'
        ? a.displayName.localeCompare(b.displayName)
        : ui.tenantSort === 'deployments'
          ? b.deployments - a.deployments
          : (b.listCostUsd ?? 0) - (a.listCostUsd ?? 0),
    );

  return (
    <Stack gap={16}>
      <Breadcrumbs separator="/" styles={{ separator: { color: T.ink4, marginInline: 8 } }}>
        <Anchor c={T.ink4} fw={700} fz={12} onClick={() => navigate('/estates')}>
          Estates
        </Anchor>
        <Text fz={12} fw={700} c={T.ink}>
          {estate.displayName}
        </Text>
      </Breadcrumbs>

      <Group justify="space-between" align="center" gap={20} wrap="wrap">
        <Group gap={14} wrap="nowrap" style={{ minWidth: 0 }}>
          <Title order={2}>{estate.displayName}</Title>
          <EstatePill estate={estate.id} size="xl" />
          <Pill tone={estate.enabled ? 'ok' : 'neutral'} size="xl">
            {estate.enabled ? 'ENABLED' : 'DISABLED'}
          </Pill>
        </Group>
        <Group gap={10}>
          <Button onClick={() => ui.openModal({ kind: 'newTenant', estateId })}>New tenant</Button>
          <Button variant="default" onClick={() => ui.openModal({ kind: 'newEstate' })}>
            New estate
          </Button>
          <Button variant="default" onClick={() => ui.set('estateTab', 'settings')}>
            Settings
          </Button>
        </Group>
      </Group>

      <Tabs
        value={ui.estateTab}
        onChange={(v) => ui.set('estateTab', (v ?? 'policy') as EstateTab)}
        classNames={{ root: uiClasses.tabs }}
        styles={{ root: { '--tab-border-color': T.lineHard } }}
      >
        <Tabs.List>
          <Tabs.Tab value="policy">Policy</Tabs.Tab>
          <Tabs.Tab value="tenants">
            Tenants{' '}
            <Text component="span" fw={600} c={T.ink5}>
              {tenantRows.length || ''}
            </Text>
          </Tabs.Tab>
          <Tabs.Tab value="deployments">
            Deployments{' '}
            <Text component="span" fw={600} c={T.ink5}>
              {deployRows.length || ''}
            </Text>
          </Tabs.Tab>
          <Tabs.Tab value="keys">
            Client keys{' '}
            <Text component="span" fw={600} c={T.ink5}>
              {keyRows.length || ''}
            </Text>
          </Tabs.Tab>
          <Tabs.Tab value="ledger">Usage ledger</Tabs.Tab>
          <Tabs.Tab value="settings">Settings</Tabs.Tab>
        </Tabs.List>
      </Tabs>

      {ui.estateTab === 'policy' ? (
        <AutoGrid min={300} gap={16}>
          <SectionCard p="18px 20px">
            <ColLabel>Memory</ColLabel>
            <Stack gap={11} mt={14}>
              <KV
                k="Mode"
                v={estate.memory?.mode === 'on' ? 'on' : 'off'}
                fg={estate.memory?.mode === 'on' ? T.teal : T.ink3}
              />
              <KV
                k="Cap per tenant"
                v={estate.memory?.mode === 'on' ? String(estate.memory?.maxMemoriesPerTenant || DEFAULT_MEMORY_CAP) : '—'}
              />
            </Stack>
            <Text fz={12} c={T.ink4} lh={1.6} mt={14}>
              Long-term agent memory (spec/memory.md). Changed with{' '}
              <code>haus memory set --estate {estate.id} --mode on|off</code>; ROUTER_MEMORY_ENABLED is the deploy lever.
            </Text>
            <Anchor
              fz={13}
              fw={700}
              mt={10}
              display="inline-block"
              onClick={() => {
                ui.setMemoryEstate(estate.id);
                navigate('/memory');
              }}
            >
              Tenants&apos; memory →
            </Anchor>
          </SectionCard>

          <SectionCard p="18px 20px">
            <ColLabel>Realms</ColLabel>
            <Stack gap={11} mt={14}>
              <KV k="Accepted realms" v={estate.realms.map((r) => r.name).join(', ')} />
              <KV k="Estate claim" v="pinned by realm" />
              <KV k="Token TTL cap" v="3650 days" />
            </Stack>
            <Text fz={12} c={T.ink4} lh={1.6} mt={14}>
              Realms retire on evidence — set RetireAfter when the router.auth.realm metric reads
              zero.
            </Text>
          </SectionCard>

          <SectionCard p="18px 20px">
            <ColLabel>Provider policy</ColLabel>
            <Stack gap={11} mt={14}>
              <KV k="Allowed providers" v={(estate.providers.allowed ?? []).join(', ') || '—'} />
              <KV k="Model deny-list" v={(estate.providers.modelDeny ?? []).join(', ') || 'none'} />
              <KV k="Tenant BYO keys" v={estate.providers.allowByoKeys ? 'allowed' : 'refused'} />
            </Stack>
            <Text fz={12} c={T.ink4} lh={1.6} mt={14}>
              ModelDeny is the per-estate kill switch for a model that turns out mispriced or
              unsuitable.
            </Text>
          </SectionCard>

          <SectionCard p="18px 20px">
            <ColLabel>Pool scope</ColLabel>
            <Stack gap={11} mt={14}>
              <KV k="Pool enabled" v={estate.pool.enabled ? 'yes' : 'no'} />
              <KV k="Draws from shared" v={estate.pool.useShared ? 'yes' : 'no'} />
              <KV k="Reserved scopes" v={(estate.pool.reserved ?? []).join(', ') || 'none'} />
            </Stack>
            <Text fz={12} c={T.ink4} lh={1.6} mt={14}>
              Reserved keys are the exception; sharing is the economic argument for consolidation.
            </Text>
          </SectionCard>

          <SectionCard p="18px 20px">
            <ColLabel>Billing &amp; limits</ColLabel>
            <Stack gap={11} mt={14}>
              <KV k="Envelope lane" v={estate.billing.eventLane} />
              <KV k="Event type" v={estate.billing.eventType} />
              <KV
                k="Rate limits"
                v={`${estate.limits.billablePerMinute} billable · ${estate.limits.attributedPerMinute} attributed / min`}
              />
              <KV
                k="Completion timeout"
                v={`${estate.limits.completeTimeoutSeconds ?? '—'}s`}
              />
            </Stack>
            <Text fz={12} c={T.ink4} lh={1.6} mt={14}>
              The router emits; the estate prices. No credits or cost live on the usage record.
            </Text>
          </SectionCard>
        </AutoGrid>
      ) : null}

      {ui.estateTab === 'tenants' ? (
        <Stack gap={12}>
          <Text fz={14} c={T.ink3} lh={1.55} maw={780}>
            This estate's own end customers. The router carries the tenant on the usage record and
            never interprets it. A tenant may run its own optional credential pool, which is
            consulted before this estate's shared keys.
          </Text>
          <Group gap={10} wrap="wrap">
            <TextInput
              value={ui.tenantSearch}
              onChange={(e) => ui.set('tenantSearch', e.currentTarget.value)}
              placeholder="Search tenant name or id…"
              leftSection={<Text c={T.muted}>⌕</Text>}
              style={{ flex: '1 1 260px', minWidth: 220, maxWidth: 420 }}
            />
            <ChipRail
              value={ui.tenantMode}
              onChange={(v) => ui.set('tenantMode', v)}
              data={[
                { value: 'all', label: 'All modes' },
                { value: 'billable', label: 'billable' },
                { value: 'attributed', label: 'attributed' },
              ]}
            />
            <ChipRail
              value={ui.tenantPool}
              onChange={(v) => ui.set('tenantPool', v)}
              data={[
                { value: 'all', label: 'All pools' },
                { value: 'own', label: 'Own pool' },
                { value: 'estate', label: 'Estate pool' },
              ]}
            />
            <Button
              variant="default"
              size="xs"
              onClick={() =>
                ui.set(
                  'tenantSort',
                  ui.tenantSort === 'cost' ? 'name' : ui.tenantSort === 'name' ? 'deployments' : 'cost',
                )
              }
            >
              Sort: {ui.tenantSort}
            </Button>
          </Group>
          <ConsoleTable cols={TENANT_COLS} minWidth={940} hover>
            {filteredTenants.map((t) => (
              <Row
                key={t.key}
                onClick={() => navigate(`/estates/${t.estateId}/tenants/${t.tenantId}`)}
              >
                <Table.Td>
                  <Text fz={14} fw={700}>
                    {t.displayName}
                  </Text>
                  <Text ff="monospace" fz={12} c={T.ink5} mt={2}>
                    {t.key}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text fz={13} fw={700} c={t.sealedKeys ? T.tealFg : T.ink5}>
                    {t.sealedKeys ? `${t.sealedKeys} sealed` : 'pool only'}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text fz={14} fw={600}>
                    {t.deployments}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text fz={13} fw={700} c={t.billingMode === 'billable' ? T.coralDeep : T.ink3}>
                    {t.billingMode}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text fz={14} fw={600}>
                    {t.listCostUsd === null ? '—' : listCost(t.listCostUsd)}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Pill tone="ok" size="lg">
                    {t.status}
                  </Pill>
                </Table.Td>
              </Row>
            ))}
            {filteredTenants.length === 0 ? (
              <TableMessage
                span={TENANT_COLS.length}
                title="Not connected — tenants are not a stored entity"
                detail="The router has no tenant collection: a tenant is a string carried on tokens, deployments and usage rows. This table will be derived from those, and is preview-only until it is."
              />
            ) : null}
          </ConsoleTable>
        </Stack>
      ) : null}

      {ui.estateTab === 'deployments' ? (
        <Stack gap={12}>
          <Text fz={14} c={T.ink3} lh={1.55} maw={780}>
            One credential-bearing install. The deployment — not the tenant — carries the policy
            that tightens this estate's, so rotating a token no longer rotates policy. Billing mode
            lives on the token record, which is why it is read from the token below.
          </Text>
          <Group justify="space-between">
            <Text fz={16} fw={800}>
              Deployments &amp; router tokens
            </Text>
            <Button
              variant="subtle"
              size="xs"
              onClick={() => ui.openModal({ kind: 'mintToken', estateId })}
            >
              Mint token
            </Button>
          </Group>
          <ConsoleTable cols={DEPLOY_COLS} minWidth={1050}>
            {deployRows.map((d) => {
              const tok = tokenFor(d.id);
              const mode = tok ? effectiveBillingMode(tok) : '—';
              return (
                <Row key={d.id}>
                  <Table.Td>
                    <Text fz={14} fw={700}>
                      {d.label}
                    </Text>
                    <Text ff="monospace" fz={12} c={T.ink5} mt={2}>
                      {d.id}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text fz={13} c={T.ink2} truncate>
                      {d.tenantId}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text fz={13} fw={700} c={mode === 'billable' ? T.coralDeep : T.ink3}>
                      {mode}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text fz={13} c={expiringSoon(tok?.expiresAt) ? T.danger : T.ink2}>
                      {dateOnly(tok?.expiresAt)}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text ff="monospace" fz={12} c={T.ink3}>
                      {tok?.tokenSha256 ?? '—'}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text
                      fz={13}
                      fw={700}
                      c={canMutate ? T.danger : T.faint}
                      ta="right"
                      title={canMutate ? undefined : NO_WRITE}
                      style={{ cursor: canMutate ? 'pointer' : 'not-allowed' }}
                    >
                      Revoke
                    </Text>
                  </Table.Td>
                </Row>
              );
            })}
            {deployRows.length === 0 ? (
              <TableMessage
                span={DEPLOY_COLS.length}
                title={
                  isUnconfigured(deployments)
                    ? 'Not connected — no read route for router_deployments'
                    : 'No deployments in this estate'
                }
              />
            ) : null}
          </ConsoleTable>
        </Stack>
      ) : null}

      {ui.estateTab === 'keys' ? (
        <Stack gap={12}>
          <Text fz={14} c={T.ink3} lh={1.55} maw={780}>
            Ed25519 client keys registered to this estate. A client key only ever yields an{' '}
            <Text component="span" fw={700}>
              attributed
            </Text>{' '}
            token, and revoking one cascades to the tokens it produced. Every mint, revoke and key
            mutation writes an audit row.
          </Text>
          <ConsoleTable cols={KEY_COLS} minWidth={880}>
            {keyRows.map((c) => (
              <Row key={c.id}>
                <Table.Td>
                  <Text fz={14} fw={700}>
                    {c.label}
                  </Text>
                  <Text ff="monospace" fz={12} c={T.ink5} mt={2}>
                    {c.id}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text fz={13} c={T.ink2} truncate>
                    {c.deploymentId}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text fz={13} c={T.ink2}>
                    {relative(c.lastUsedAt)}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text fz={13} c={expiringSoon(c.expiresAt) ? T.danger : T.ink2}>
                    {dateOnly(c.expiresAt)}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text
                    fz={13}
                    fw={700}
                    c={canMutate ? T.danger : T.faint}
                    ta="right"
                    title={canMutate ? undefined : NO_WRITE}
                  >
                    Revoke
                  </Text>
                </Table.Td>
              </Row>
            ))}
            {keyRows.length === 0 ? (
              <TableMessage
                span={KEY_COLS.length}
                title={
                  isUnconfigured(clientKeys)
                    ? 'Not connected — no read route for router_keys'
                    : 'No client keys registered to this estate'
                }
              />
            ) : null}
          </ConsoleTable>
        </Stack>
      ) : null}

      {ui.estateTab === 'ledger' ? (
        <Stack gap={12}>
          <Text fz={14} c={T.ink3} lh={1.55} maw={780}>
            Every served completion for {estate.displayName}, billable and attributed alike,
            straight from the durable ledger. This is the estate-scoped view of{' '}
            <Text component="span" ff="monospace">
              GET /v1/usage
            </Text>{' '}
            — the same rows the estate can pull itself to backfill a collector gap.
          </Text>
          <Button variant="subtle" size="xs" w="fit-content" onClick={() => navigate('/ledger')}>
            Open the full ledger →
          </Button>
        </Stack>
      ) : null}

      {ui.estateTab === 'settings' ? (
        <AutoGrid min={340} gap={16} align="start">
          <SectionCard p={20} title="Payload capture">
            <Text fz={12} c={T.ink4} lh={1.6} mt={5}>
              Prompt and response bodies pass through the router to reach the provider. Capture
              writes them to a separate short-retention store for debugging. Turning it on stores
              your customers' content in a service that already holds every provider credential,
              so it is off unless an estate asks for it, it expires on the retention below, and
              every operator read of a stored payload is audited.
            </Text>
            <Group mt={18}>
              <ChipRail
                value={captureMode}
                onChange={(v) => void changeCapture(v)}
                data={[
                  { value: 'off', label: 'Off' },
                  { value: 'errors', label: 'Errors only' },
                  { value: 'sampled', label: 'Sampled' },
                  { value: 'full', label: 'Full' },
                ]}
                fullWidth
              />
            </Group>
            {captureError ? (
              <Text fz={12} c={T.danger} lh={1.6} mt={9}>
                {captureError}
              </Text>
            ) : (
              <Text fz={12} c={T.ink4} lh={1.6} mt={9}>
                {captureMode === 'off'
                  ? 'Nothing is stored for this estate.'
                  : `Storing ${
                      captureMode === 'errors'
                        ? 'failed completions only'
                        : captureMode === 'sampled'
                          ? `${Math.round((estate.capture?.sampleRate ?? 0) * 100)}% of completions`
                          : 'every completion'
                    }, kept ${estate.capture?.retentionHours || 168}h. The router applies a change from its next request.`}
              </Text>
            )}
            <Stack gap={0} mt={18}>
              {[
                ['Redact on write', 'Not built: a stored payload is written as it passed through.'],
                ['Tenants may override', 'Would let each tenant turn capture on for its own traffic, or off.'],
                ['Seal under tenant DEK', "Would encrypt each tenant's payloads with its own key."],
              ].map(([label, note]) => (
                <Group
                  key={label}
                  justify="space-between"
                  align="flex-start"
                  gap={16}
                  py={12}
                  style={{ borderTop: `1px solid ${T.rowLine}` }}
                >
                  <Stack gap={3}>
                    <Text fz={13} fw={700}>
                      {label}
                    </Text>
                    <Text fz={12} c={T.ink4} lh={1.55}>
                      {note}
                    </Text>
                  </Stack>
                  <Switch
                    checked={false}
                    readOnly
                    disabled
                    classNames={{ input: uiClasses.switchInput, track: uiClasses.switchTrack }}
                    styles={{
                      root: {
                        '--switch-height': '21px',
                        '--switch-width': '38px',
                        '--switch-thumb-size': '16px',
                      },
                    }}
                    aria-label={label}
                  />
                </Group>
              ))}
            </Stack>
          </SectionCard>

          <Stack gap={16}>
            <SectionCard p={20} title="Providers & models">
              <Text fz={13} fw={700} mt={16} mb={8}>
                Allowed providers
              </Text>
              <Group gap={8} wrap="wrap">
                {['anthropic', 'openai', 'meta', 'kimi-code', 'moonshot', 'deepseek', 'xai', 'gemini'].map((p) => {
                  const on = (estate.providers.allowed ?? []).includes(p);
                  return (
                    <Group
                      key={p}
                      gap={7}
                      style={{
                        fontSize: 12,
                        fontWeight: 700,
                        padding: '7px 13px',
                        borderRadius: 999,
                        border: `1px solid ${on ? T.coralBorder : T.border}`,
                        background: on ? T.coralSoft : T.card,
                        color: on ? T.coralDeep : T.ink4,
                      }}
                    >
                      <Text component="span" fz={11}>
                        {on ? '✓' : '+'}
                      </Text>
                      {p}
                    </Group>
                  );
                })}
              </Group>
              <Text fz={13} fw={700} mt={18} mb={7}>
                Model deny-list
              </Text>
              <TextInput
                readOnly
                value={(estate.providers.modelDeny ?? []).join(', ')}
                placeholder="none"
              />
              <Text fz={12} c={T.ink4} lh={1.55} mt={8}>
                The per-estate kill switch for a model that turns out mispriced or unsuitable for
                this audience.
              </Text>
            </SectionCard>

            <SectionCard p={20} title="Limits & billing">
              <AutoGrid min={120} gap={12} mt={16}>
                <TextInput
                  label="Billable / min"
                  readOnly
                  value={String(estate.limits.billablePerMinute)}
                />
                <TextInput
                  label="Attributed / min"
                  readOnly
                  value={String(estate.limits.attributedPerMinute)}
                />
                <TextInput
                  label="Timeout"
                  readOnly
                  value={`${estate.limits.completeTimeoutSeconds ?? '—'}s`}
                />
              </AutoGrid>
              <Text fz={13} fw={700} mt={18} mb={7}>
                Envelope lane
              </Text>
              <Text
                ff="monospace"
                fz={14}
                fw={700}
                c={T.ink2}
                style={{ background: T.rail, borderRadius: 9, padding: '11px 13px' }}
              >
                {estate.billing.eventLane}
              </Text>
              <Text fz={12} c={T.ink4} lh={1.55} mt={8}>
                The lane is pinned for the life of the estate — changing it would orphan every
                in-flight event id. Migrate on a new estate instead.
              </Text>
            </SectionCard>
          </Stack>
        </AutoGrid>
      ) : null}
    </Stack>
  );
}

export { tenantKey };
